import { useEffect, useMemo } from 'react';
import {
  useReduxAppDispatch,
  useReduxAppSelector,
} from '@/data-store/redux/redux-store';
import {
  setUserLocation as setUserLocationAction,
  type UserLocation,
} from '@/data-store/redux/slices/userLocationSlice';

const ecommStatesList = ['FL', 'NV', 'NJ', 'TX'];

enum Location {
  COUNTRY = 'user_country',
  STATE = 'user_state',
  ZIP_CODE = 'user_zip_code',
  CITY = 'user_city',
}

export const getCloudfrontUserLocation = (): UserLocation => ({
  country: window.cloudFrontUserCountry ?? null,
  state: window.cloudFrontUserState ?? null,
  zipCode: window.cloudFrontUserZipCode ?? null,
  city: window.cloudFrontUserCity ?? null,
});

const getSessionStorageItem = (key: string): string | null => {
  return JSON.parse(sessionStorage.getItem(key) ?? 'null');
};
const setSessionStorageItem = (key: string, value: string | null) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

const getSessionStorageUserLocation = (): UserLocation | null => {
  const location = {
    country: getSessionStorageItem(Location.COUNTRY),
    state: getSessionStorageItem(Location.STATE),
    zipCode: getSessionStorageItem(Location.ZIP_CODE),
    city: getSessionStorageItem(Location.CITY),
  };
  const hasLocation = location.country && location.state && location.zipCode;

  return hasLocation ? location : null;
};

const setSessionStorageUserLocation = (location: UserLocation) => {
  setSessionStorageItem(Location.COUNTRY, location.country);
  setSessionStorageItem(Location.STATE, location.state);
  setSessionStorageItem(Location.ZIP_CODE, location.zipCode);
  setSessionStorageItem(Location.CITY, location.city);
};

const useEcommMarket = () => {
  const dispatch = useReduxAppDispatch();
  const { country, state, zipCode, city, status } = useReduxAppSelector(
    (state) => state.UserLocation
  );

  useEffect(() => {
    if (status !== 'pending' || country || state || zipCode) return;

    const cachedLocation = getSessionStorageUserLocation();
    if (cachedLocation) {
      dispatch(setUserLocationAction(cachedLocation));
      return;
    }

    if (!cachedLocation && window.cloudFrontUserState) {
      dispatch(setUserLocationAction(getCloudfrontUserLocation()));
      return;
    }

    // The geo script fetches CloudFront headers async — wait for it to settle
    // so all-null values afterwards mean "no location found", not "still loading"
    let cancelled = false;
    const resolveCloudfrontLocation = () => {
      if (!cancelled) {
        dispatch(setUserLocationAction(getCloudfrontUserLocation()));
      }
    };

    if (window.cloudFrontUserLocationPromise) {
      window.cloudFrontUserLocationPromise
        .then(resolveCloudfrontLocation)
        .catch(resolveCloudfrontLocation);
    } else {
      resolveCloudfrontLocation();
    }
    return () => {
      // Effect re-ran or unmounted before the fetch settled — ignore its result
      // so stale CloudFront values can't overwrite a newer location
      cancelled = true;
    };
  }, [dispatch, status, country, state, zipCode]);

  const setUserLocation = (userLocation: NonNullable<UserLocation>) => {
    dispatch(setUserLocationAction(userLocation));
    setSessionStorageUserLocation(userLocation);
  };

  const isInEcommMarket = state && ecommStatesList.includes(state);
  const userLocation = useMemo(
    () => ({ country, state, zipCode, city }),
    [country, state, zipCode, city]
  );

  return {
    isInEcommMarket,
    isLocationPending: status === 'pending',
    userLocation,
    setUserLocation,
  };
};

export default useEcommMarket;
